import React from 'react'
import { Image, ImageBackground, StyleSheet } from 'react-native'

import { useNavigation } from '@react-navigation/native'
import { useToast } from '@zet/common/components/toast'
import { useDownloadAndShare } from '@zet/common/hooks/useDownloadAndShare'
import {
  B2,
  B4,
  BTN,
  H5,
  Icon,
  Pressable,
  SH1,
  Stack,
  XStack,
  YStack,
  colors,
} from '@zet/design-system'
import Avatar from '@zet/design-system/avatar/Avatar'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import { DEVICE_HEIGHT, DEVICE_WIDTH } from '@zet/utils/Constants'

import { CARD_BACKGROUND, SCREEN_BACKGROUND } from '../../../assets/images'

const IdCard = () => {
  const { data } = useUserContext()
  const toast = useToast()
  const { goBack } = useNavigation()

  const onSuccess = () => {
    toast?.show({
      intent: 'success',
      heading: i18n.t('toastMessages.success'),
      description: i18n.t('visitingCard.successMsg'),
      hideAfter: 3000,
    })
  }

  const { ref, download, share } = useDownloadAndShare({
    width: DEVICE_WIDTH * 0.8 * 3,
    height: DEVICE_HEIGHT * 0.58 * 3,
    onSuccess: onSuccess,
  })

  return (
    <Stack className="bg-tertiary-200 flex-1">
      <ImageBackground source={SCREEN_BACKGROUND} style={styles.imageStyles}>
        <YStack className="mt-3 flex-1 px-3">
          <XStack className="mb-3 space-x-3">
            <Pressable
              className="bg-grays-10 rounded-md"
              onPress={goBack as never}
            >
              <Icon type="ArrowLeft" color={colors.grays[60]} size={32} />
            </Pressable>
            <H5 className="text-white">{i18n.t('idCard.myIdCard')}</H5>
          </XStack>
          <Stack
            ref={ref as never}
            collapsable={false}
            className="mt-2 items-center"
          >
            <ImageBackground
              source={CARD_BACKGROUND}
              style={styles.cardStyle}
              resizeMethod="resize"
              resizeMode="contain"
            >
              <YStack>
                <B2 className="mt-3 text-center text-black">
                  {data?.companyName}
                </B2>
                <XStack className="mb-2 mt-3 items-end justify-center">
                  <Stack>
                    {data?.imageUrl ? (
                      <Image
                        source={{ uri: data?.imageUrl }}
                        className="border-grays-40 h-28 w-28 rounded-md border-2"
                        resizeMode="stretch"
                        resizeMethod="scale"
                      />
                    ) : (
                      <Stack className="items-center">
                        <Avatar name={data?.name as string} size="maxLarge" />
                      </Stack>
                    )}
                  </Stack>
                </XStack>
                <Stack className="self-center">
                  <SH1 className="text-grays-100 text-center">{data?.name}</SH1>
                  <B2 className="text-grays-80">
                    {i18n.t('idComponent.businessConsult')}
                  </B2>
                </Stack>
                <Stack className="my-2">
                  <Stack className="bg-grays-30 px-4/2 self-center rounded-lg py-2">
                    <XStack className="items-center space-x-1">
                      <Icon type="PhoneFilled" size={18} />
                      <B4 className="text-black">+91 - {data?.phoneNumber}</B4>
                    </XStack>
                    {data?.email && (
                      <XStack className="mt-1 items-center space-x-1">
                        <Icon type="Email" size={18} />
                        <B4 className="text-black">{data?.email}</B4>
                      </XStack>
                    )}
                  </Stack>
                </Stack>

                {data?.customerAdditionQRUrl && (
                  <Stack
                    className={`items-center justify-end self-center ${
                      data?.email ? '' : 'mt-3'
                    } `}
                  >
                    <Image
                      source={{ uri: data?.customerAdditionQRUrl }}
                      style={styles.qrStyle}
                      resizeMode="contain"
                    />
                    <B2 className="text-center text-black">
                      {i18n.t('idComponent.scanToAdd')}
                    </B2>
                  </Stack>
                )}
              </YStack>
            </ImageBackground>
          </Stack>
          <B2 className="mt-2 text-center text-white">
            {i18n.t('buttonGroup.freeDownload')}
          </B2>
          <Stack className="mb-3 flex-1 justify-end">
            <Pressable
              className="py-4/2 flex-row justify-center rounded-lg bg-white"
              onPress={download}
            >
              <XStack className="space-x-2">
                <Icon type="Download" color={colors.primary[100]} />
                <BTN className="text-primary-100">
                  {i18n.t('buttonGroup.download')}
                </BTN>
              </XStack>
            </Pressable>
            <Pressable
              className="bg-primary-100 py-4/2 mt-3 flex-row justify-center rounded-lg"
              onPress={share}
            >
              <XStack className="space-x-2">
                <Icon type="Share" color={colors.neutral.white100} />
                <BTN className="text-white">
                  {i18n.t('marketingResources.cta')}
                </BTN>
              </XStack>
            </Pressable>
          </Stack>
        </YStack>
      </ImageBackground>
    </Stack>
  )
}

export default IdCard

const styles = StyleSheet.create({
  imageStyles: { height: '100%', flex: 1 },
  cardStyle: { height: DEVICE_HEIGHT * 0.58, width: DEVICE_WIDTH * 0.8 },
  editButtonStyle: { right: '30%', bottom: -5 },
  qrStyle: { height: 110, width: 110 },
})
